import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  listAllProducts,
  deleteProduct,
  reorderProducts,
} from "@/lib/admin.functions";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { PageHeader, type Prod } from "@/components/admin/shared";
import { SortableProductList } from "@/components/admin/SortableProductList";

export const Route = createFileRoute("/admin/products/")({
  component: ProductsListPage,
});

function ProductsListPage() {
  const list = useServerFn(listAllProducts);
  const del = useServerFn(deleteProduct);
  const reorder = useServerFn(reorderProducts);
  const navigate = useNavigate();
  const [products, setProducts] = useState<Prod[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      setProducts((await list({})) as unknown as Prod[]);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const remove = async (id: string) => {
    try {
      await del({ data: { id } });
      toast.success("Borttagen");
      await reload();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleReorder = async (ids: string[]) => {
    const map = new Map(products.map((p) => [p.id!, p]));
    setProducts(ids.map((id, i) => ({ ...(map.get(id) as Prod), sort_order: i })));
    try {
      await reorder({ data: { ids } });
    } catch (e: any) {
      toast.error(`Kunde inte spara ordning: ${e.message}`);
      await reload();
    }
  };

  return (
    <>
      <PageHeader title={`Produkter (${products.length})`}>
        <button
          onClick={() => navigate({ to: "/admin/products/new" })}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2.5 text-sm font-bold w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" /> Ny produkt
        </button>
      </PageHeader>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : products.length === 0 ? (
        <p className="text-sm text-muted-foreground">Inga produkter ännu.</p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3">
            Dra raderna med <span className="font-bold">⋮⋮</span>-handtaget för att ändra ordning. Ordningen sparas automatiskt.
          </p>
          <SortableProductList
            products={products}
            onReorder={handleReorder}
            onDelete={remove}
          />
        </>
      )}
    </>
  );
}
